import {WriteButton} from '../common/buttons/WriteActions';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, Button, Card, CardActionArea, CardContent, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, MenuItem, Select, Skeleton, Snackbar, Stack, Table, TableBody, TableContainer, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {useNavigate} from 'react-router-dom';
import {HomeForecastPeriod, InventoryItemType, InventoryUnit, IPurchaseForecastItem} from '../../models/Home/IHomeDashboard';
import {homeAPI} from '../../services/HomeServices';
import {OrdersByDayGuid} from '../orders/order/OrderByDayGuid';
import {getOrderDateKey, getTodayDateKey} from '../orders/order/orderDisplay';
import {PurchaseEstimatedCost} from './PurchaseEstimatedCost';
import {useCurrency} from '../../hooks/useCurrency';
import {usePageLimit} from '../../hooks/usePageLimit';
import {copyTextToClipboard} from '../../common/clipboard';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {getApiErrorKind, getApiErrorMessage, getApiFieldErrors, getApiTraceId} from '../../common/apiErrors';
import TraceIdFeedback from '../feedback/TraceIdFeedback';
import {Surface} from '../common/layout/Surface';

const Section = ({title, children}: {title: string; children: React.ReactNode}) => <Box component="section"><Typography variant="h3" sx={{mb: 1.25}}>{title}</Typography>{children}</Box>;
const dateKey = (value: string) => new Date(value).toISOString().slice(0, 10);
const DASHBOARD_DAYS_OPTIONS = [7, 10, 14, 30, 60, 90];
const FORECAST_PERIOD_KEY = 'home_forecast_period';
const MIN_DASHBOARD_DAYS = 1;
const MAX_DASHBOARD_DAYS = 365;
const isValidDays = (days: number) => Number.isInteger(days) && days >= MIN_DASHBOARD_DAYS && days <= MAX_DASHBOARD_DAYS;

const HomePage = () => {
    const {t, i18n} = useAppTranslation();
    const navigate = useNavigate();
    const {formatCurrency} = useCurrency();
    const [storedDashboardDays, setDashboardDays] = usePageLimit('home', 7);
    const dashboardDays = isValidDays(storedDashboardDays) ? storedDashboardDays : 7;
    const forecastOptions = useMemo(() => [...new Set([dashboardDays, 14, 30].filter(days => days >= dashboardDays))], [dashboardDays]);
    const [period, setPeriodState] = useState<HomeForecastPeriod>(() => {
        const saved = Number(localStorage.getItem(FORECAST_PERIOD_KEY));
        return Number.isFinite(saved) && forecastOptions.includes(saved) ? saved : dashboardDays;
    });
    const [openOrderId, setOpenOrderId] = useState<string | null>(() => {
        const storedOrderId = sessionStorage.getItem('homeOpenOrderId');
        return storedOrderId || null;
    });
    const [openDate, setOpenDate] = useState<string | null>(() => sessionStorage.getItem('homeOpenDate'));
    const initializedOrderDays = useRef(false);
    const restoredOrderScroll = useRef(false);
    const [openProductionDate, setOpenProductionDate] = useState<string | null>(null);
    const initializedProductionDays = useRef(false);
    const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
    const [inventoryForecastOpen, setInventoryForecastOpen] = useState(false);
    const [copyState, setCopyState] = useState<'success' | 'error' | null>(null);
    const [showIssuesOnly, setShowIssuesOnly] = useState(false);
    const [serverToast, setServerToast] = useState('');
    const [serverTraceId, setServerTraceId] = useState('');
    const dashboard = homeAPI.useGetDashboardQuery(dashboardDays);
    const forecast = homeAPI.useGetInventoryForecastQuery(period);
    const periodOrderDays = useMemo(() => {
        const from = getTodayDateKey();
        const untilDate = new Date();
        untilDate.setDate(untilDate.getDate() + dashboardDays - 1);
        const until = getOrderDateKey(untilDate.toISOString());
        return (dashboard.data?.orders ?? [])
        .filter(day => {
            const key = getOrderDateKey(day.date);
            return key >= from && key <= until;
        });
    }, [dashboardDays, dashboard.data?.orders]);
    const displayedOrderDays = useMemo(() => periodOrderDays
        .map(day => ({
            ...day,
            orders: showIssuesOnly
                ? day.orders.filter(order => order.hasShortage || !!order.missingStock?.length)
                : day.orders,
        }))
        .filter(day => day.orders.length),
    [periodOrderDays, showIssuesOnly]);
    const orders = useMemo(() => periodOrderDays.flatMap(day => day.orders), [periodOrderDays]);
    const orderDays = useMemo(() => {
        let displayedWeekIndex = 0;
        return displayedOrderDays.map((day, index, days) => {
            const weekKey = `${getOrderDateKey(day.date).slice(0, 4)}-${day.week.number}`;
            const previousDay = index > 0 ? days[index - 1] : undefined;
            const previousWeekKey = previousDay
                ? `${getOrderDateKey(previousDay.date).slice(0, 4)}-${previousDay.week.number}`
                : weekKey;
            const startsNewWeek = index > 0 && previousWeekKey !== weekKey;
            if (startsNewWeek) displayedWeekIndex += 1;

            return {
                day,
                startsNewWeek,
                separatesDay: index > 0 && !startsNewWeek,
                alternateWeek: displayedWeekIndex % 2 === 1,
            };
        });
    }, [displayedOrderDays]);
    const today = dateKey(new Date().toISOString());
    const formatDate = (value: string) => new Intl.DateTimeFormat(i18n.resolvedLanguage, {day: 'numeric', month: 'long'}).format(new Date(value));
    const formatQuantity = (quantity: number, unit: InventoryUnit) => unit === 'G' && Math.abs(quantity) >= 1000
        ? `${Number((quantity / 1000).toFixed(2))} ${t('home:kilograms')}`
        : `${Number(quantity.toFixed(2))} ${unit === 'G' ? t('home:grams') : t('home:pieces')}`;

    useEffect(() => {
        if (storedDashboardDays !== dashboardDays) setDashboardDays(dashboardDays);
    }, [storedDashboardDays, dashboardDays, setDashboardDays]);

    useEffect(() => {
        const error = dashboard.error || forecast.error;
        if (!error || getApiErrorKind(error) !== 'server') return;
        setServerToast(getApiErrorMessage(error, t));
        setServerTraceId(getApiTraceId(error));
    }, [dashboard.error, forecast.error, t]);

    useEffect(() => {
        if (forecastOptions.includes(period)) return;
        setPeriodState(dashboardDays);
        localStorage.setItem(FORECAST_PERIOD_KEY, String(dashboardDays));
    }, [dashboardDays, forecastOptions, period]);

    useEffect(() => {
        if (dashboard.isLoading) return;
        const days = displayedOrderDays;
        if (!days.length) {
            setOpenDate(null);
            setOpenOrderId(null);
            return;
        }

        const availableDates = new Set(days.map(day => getOrderDateKey(day.date)));
        if (!initializedOrderDays.current) {
            initializedOrderDays.current = true;
            const todayKey = getTodayDateKey();
            if (availableDates.has(todayKey)) setOpenDate(todayKey);
            return;
        }
        if (openDate && availableDates.has(openDate)) return;
        if (openDate) {
            setOpenDate(null);
            setOpenOrderId(null);
        }
    }, [dashboard.isLoading, displayedOrderDays, openDate]);

    useEffect(() => {
        if (dashboard.isLoading || restoredOrderScroll.current || !openDate || openOrderId === null) return;
        const storedScroll = sessionStorage.getItem('homeScrollY');
        if (!storedScroll) return;
        restoredOrderScroll.current = true;
        requestAnimationFrame(() => requestAnimationFrame(() => {
            window.scrollTo({top: Number(storedScroll), behavior: 'auto'});
            sessionStorage.removeItem('homeOpenDate');
            sessionStorage.removeItem('homeOpenOrderId');
            sessionStorage.removeItem('homeScrollY');
        }));
    }, [dashboard.isLoading, openDate, openOrderId]);

    const setPeriod = (days: HomeForecastPeriod) => {
        setPeriodState(days);
        setInventoryForecastOpen(false);
        localStorage.setItem(FORECAST_PERIOD_KEY, String(days));
    };

    const changeDashboardDays = (days: number) => {
        if (!isValidDays(days)) return;
        setDashboardDays(days);
        setPeriod(days);
    };

    const production = useMemo(() => (dashboard.data?.orders ?? []).map(day => {
        const totals = new Map<string, number>();
        day.orders.forEach(order => order.subOrders.forEach(item => totals.set(item.recipeName ?? '', (totals.get(item.recipeName ?? '') ?? 0) + Number(item.count || 0))));
        return {date: day.date, items: [...totals.entries()]};
    }).filter(day => day.items.length), [dashboard.data]);

    useEffect(() => {
        if (!production.length) {
            setOpenProductionDate(null);
            return;
        }

        const availableDates = new Set(production.map(day => getOrderDateKey(day.date)));
        if (!initializedProductionDays.current) {
            initializedProductionDays.current = true;
            const todayKey = getTodayDateKey();
            if (availableDates.has(todayKey)) setOpenProductionDate(todayKey);
            return;
        }
        if (openProductionDate && !availableDates.has(openProductionDate)) setOpenProductionDate(null);
    }, [openProductionDate, production]);

    const purchasesByDate = useMemo(() => {
        const groups = new Map<string, IPurchaseForecastItem[]>();
        for (const item of forecast.data?.purchases ?? []) {
            const key = item.requiredBy;
            groups.set(key, [...(groups.get(key) ?? []), item]);
        }
        return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
    }, [forecast.data]);

    const purchaseTotals = useMemo(() => {
        const totals = new Map<string, {type: InventoryItemType; itemId: string; name: string; unit: InventoryUnit; quantity: number}>();
        for (const item of forecast.data?.purchases ?? []) {
            const key = `${item.type}-${item.itemId}-${item.unit}`;
            const current = totals.get(key);
            totals.set(key, {
                type: item.type,
                itemId: item.itemId,
                name: item.name,
                unit: item.unit,
                quantity: (current?.quantity ?? 0) + item.quantity,
            });
        }
        return [...totals.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [forecast.data]);
    const forecastShortages = useMemo(() => [
        ...(forecast.data?.ingredients ?? []).map(item => ({...item, type: 'ingredient' as const})),
        ...(forecast.data?.packages ?? []).map(item => ({...item, type: 'package' as const})),
    ].filter(item => item.shortageQuantity > 0), [forecast.data]);
    const itemLabel = (item: {name: string; type: InventoryItemType}) => `${item.name} (${t(item.type === 'package' ? 'home:package' : 'home:ingredient')})`;

    const estimatedPurchaseCost = forecast.data?.estimatedPurchaseCost ?? 0;

    const purchaseListText = useMemo(() => [
        t('home:purchase_total_copy'),
        `${t('home:estimated_purchase_cost')}: ${formatCurrency(estimatedPurchaseCost)}`,
        ...purchaseTotals.map(item => `${itemLabel(item)} — ${formatQuantity(item.quantity, item.unit)}`),
        '',
        ...purchasesByDate.flatMap(([requiredBy, items]) => [
            t('home:required_by', {date: formatDate(requiredBy)}),
            ...items.map(item => `${itemLabel(item)} — ${formatQuantity(item.quantity, item.unit)}`),
            '',
        ]),
    ].join('\n').trim(), [purchaseTotals, purchasesByDate, i18n.resolvedLanguage, estimatedPurchaseCost, formatCurrency, t]);

    const copyPurchaseList = async () => {
        try {
            await copyTextToClipboard(purchaseListText);
            setCopyState('success');
        } catch {
            setCopyState('error');
        }
    };

    if (dashboard.isLoading) return <Stack spacing={2}><Skeleton variant="rounded" height={48}/><Skeleton variant="rounded" height={110}/><Skeleton variant="rounded" height={260}/></Stack>;
    if (dashboard.isError) {
        const fieldError = getApiFieldErrors(dashboard.error, t).days;
        return <>
            <Alert severity="error" action={<Button onClick={() => dashboard.refetch()}>{t('home:retry')}</Button>} sx={{m: 2}}>
                {fieldError || getApiErrorMessage(dashboard.error, t)}
            </Alert>
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={serverTraceId}/></Alert>
            </Snackbar>
        </>;
    }

    const summary = dashboard.data?.summary;
    return <Stack spacing={{xs: 3, md: 4}} sx={{width: '100%', pb: 3}}>
        <WriteButton
            fullWidth
            variant="contained"
            size="large"
            startIcon={<AddIcon/>}
            onClick={() => navigate('/app/orders/new', {state: {fromHome: true}})}
        >
            {t('home:create_order')}
        </WriteButton>

        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1}}>
            {[[summary?.ordersToday ?? 0, t('home:today'), false], [summary?.upcomingOrders ?? 0, t('home:upcoming'), false], [summary?.issuesCount ?? 0, t('home:issues'), true]].map(([value, label, isIssue]) => {
                const content = <CardContent sx={{p: 1.25, '&:last-child': {pb: 1.25}, textAlign: 'center'}}>
                    <Typography variant="h3" color={isIssue && Number(value) ? 'warning.main' : 'text.primary'}>{value}</Typography>
                    <Typography variant="caption">{label}</Typography>
                </CardContent>;
                return <Card
                    key={String(label)}
                    variant="outlined"
                    sx={isIssue ? {
                        position: 'relative',
                        ...(showIssuesOnly
                            ? {borderColor: 'warning.main', bgcolor: 'rgba(255, 152, 0, 0.08)'}
                            : Number(value) ? {
                                animation: 'issuesPulse 2.4s ease-in-out infinite',
                                '@keyframes issuesPulse': {
                                    '0%, 100%': {borderColor: 'divider', boxShadow: '0 0 0 0 rgba(237, 108, 2, 0)'},
                                    '50%': {borderColor: 'warning.main', boxShadow: '0 0 0 4px rgba(237, 108, 2, 0.12)'},
                                },
                                '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
                            } : {}),
                    } : undefined}
                >
                    {isIssue
                        ? <CardActionArea
                            disabled={!Number(value)}
                            aria-pressed={showIssuesOnly}
                            onClick={() => {
                                const nextShowIssuesOnly = !showIssuesOnly;
                                const allDays = periodOrderDays;
                                const nextOpenDay = nextShowIssuesOnly
                                    ? allDays.find(day => day.orders.some(order => order.hasShortage || !!order.missingStock?.length))
                                    : allDays.find(day => getOrderDateKey(day.date) === getTodayDateKey());
                                setShowIssuesOnly(nextShowIssuesOnly);
                                setOpenDate(nextOpenDay ? getOrderDateKey(nextOpenDay.date) : null);
                                setOpenOrderId(null);
                            }}
                        >{content}</CardActionArea>
                        : content}
                    {isIssue && showIssuesOnly && <IconButton
                        size="small"
                        color="error"
                        aria-label={t('common:close')}
                        title={t('common:close')}
                        onClick={() => {
                            const todayDay = periodOrderDays
                                .find(day => getOrderDateKey(day.date) === getTodayDateKey());
                            setShowIssuesOnly(false);
                            setOpenDate(todayDay ? getOrderDateKey(todayDay.date) : null);
                            setOpenOrderId(null);
                        }}
                        sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            zIndex: 1,
                            color: 'error.main',
                            bgcolor: 'rgba(211, 47, 47, 0.10)',
                            border: '1px solid rgba(211, 47, 47, 0.24)',
                            '&:hover': {bgcolor: 'rgba(211, 47, 47, 0.18)'},
                        }}
                    >
                        <CloseIcon fontSize="small"/>
                    </IconButton>}
                </Card>;
            })}
        </Box>

        <Section title={t('home:upcoming_orders')}>
            <FormControl fullWidth size="small" sx={{mb: 1.5}}>
                <InputLabel id="dashboard-days-label">{t('home:show_period')}</InputLabel>
                <Select labelId="dashboard-days-label" value={dashboardDays} label={t('home:show_period')}
                        onChange={event => changeDashboardDays(Number(event.target.value))}>
                    {DASHBOARD_DAYS_OPTIONS.map(days => <MenuItem key={days} value={days}>{t('home:days', {count: days})}</MenuItem>)}
                </Select>
            </FormControl>
            {!orders.length ? <Alert severity="info">{t('home:no_upcoming_orders')}</Alert> : showIssuesOnly && !orderDays.length ? <Alert severity="success">{t('home:no_shortage')}</Alert> : <Surface>
                <TableContainer sx={{overflowX: 'hidden'}}>
                    <Table aria-label={t('home:upcoming_orders')} sx={{tableLayout: 'fixed', width: '100%'}}>
                        <TableBody>
                            {orderDays.map(({day, startsNewWeek, separatesDay, alternateWeek}) => <OrdersByDayGuid
                                key={day.date}
                                ordersByDay={day}
                                startsNewWeek={startsNewWeek}
                                separatesDay={separatesDay}
                                alternateWeek={alternateWeek}
                                isOpen={openDate === getOrderDateKey(day.date)}
                                openOrderId={openOrderId}
                                onToggleDate={date => {
                                    const key = getOrderDateKey(date);
                                    setOpenDate(current => current === key ? null : key);
                                    setOpenOrderId(null);
                                }}
                                onToggleOrder={id => setOpenOrderId(current => current === id ? null : id)}
                                getEditPath={id => `/app/orders/${id}/edit`}
                                editReturnPath="/"
                            />)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Surface>}
        </Section>

        <Section title={t('home:what_to_prepare')}>
            {!production.length ? <Typography color="text.secondary">{t('home:production_empty')}</Typography> : <Stack spacing={1}>{production.map(day => {
                const key = getOrderDateKey(day.date);
                const isOpen = openProductionDate === key;
                return <Card key={day.date} variant="outlined" sx={{overflow: 'hidden'}}>
                    <Box
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen}
                        onClick={() => setOpenProductionDate(current => current === key ? null : key)}
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setOpenProductionDate(current => current === key ? null : key);
                            }
                        }}
                        sx={{cursor: 'pointer', px: {xs: 1, sm: 1.5}, py: 1.25}}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <IconButton size="small" tabIndex={-1} aria-label={isOpen ? 'collapse day' : 'expand day'}>
                                <ExpandMoreIcon sx={{transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 180ms'}}/>
                            </IconButton>
                            <Typography fontWeight={700} noWrap sx={{minWidth: 0}}>
                                {key === today ? `${t('home:today')} · ` : ''}{formatDate(day.date)}
                            </Typography>
                            <Chip
                                size="small"
                                label={t('recipes:recipe_count', {count: day.items.length})}
                                sx={{ml: 'auto!important', flexShrink: 0, fontWeight: 700, bgcolor: 'rgba(154, 90, 60, 0.14)', color: 'primary.dark'}}
                            />
                        </Stack>
                    </Box>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <CardContent sx={{pt: 0, px: {xs: 1.5, sm: 2}, '&:last-child': {pb: 1.5}}}>
                            <Divider sx={{mb: 1}}/>
                            {day.items.map(([name, count]) => <Typography key={name}>{name || t('home:product')} — {count} {t('home:pieces')}</Typography>)}
                        </CardContent>
                    </Collapse>
                </Card>;
            })}</Stack>}
        </Section>

        <Section title={t('home:forecast_and_purchase')}>
            <ToggleButtonGroup exclusive fullWidth value={period} onChange={(_, value) => value && setPeriod(value)} size="small" sx={{mb: 2}}>{forecastOptions.map(days => <ToggleButton key={days} value={days}>{t('home:days', {count: days})}</ToggleButton>)}</ToggleButtonGroup>
            {forecast.isFetching ? <Stack spacing={1}><Skeleton/><Skeleton/><Skeleton/></Stack> : forecast.isError ? <Alert severity="error" action={<Button onClick={() => forecast.refetch()}>{t('home:retry')}</Button>}>{getApiFieldErrors(forecast.error, t).days || getApiErrorMessage(forecast.error, t)}</Alert> : forecast.data && <Stack spacing={2}>
                {!forecastShortages.length ? <Alert severity="success">{t('home:no_shortage')}</Alert> : <Card variant="outlined" sx={{overflow: 'hidden', borderColor: 'warning.light', bgcolor: 'rgba(255, 152, 0, 0.04)'}}>
                    <Box
                        role="button"
                        tabIndex={0}
                        aria-expanded={inventoryForecastOpen}
                        onClick={() => setInventoryForecastOpen(current => !current)}
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setInventoryForecastOpen(current => !current);
                            }
                        }}
                        sx={{cursor: 'pointer', px: {xs: 1, sm: 1.5}, py: 1.25}}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <IconButton size="small" tabIndex={-1} aria-label={inventoryForecastOpen ? 'collapse forecast' : 'expand forecast'}>
                                <ExpandMoreIcon sx={{transform: inventoryForecastOpen ? 'rotate(180deg)' : 'none', transition: 'transform 180ms'}}/>
                            </IconButton>
                            <Typography fontWeight={800}>{t('home:stock_missing')}</Typography>
                            <Chip size="small" color="warning" label={forecastShortages.length} sx={{ml: 'auto!important', fontWeight: 700}}/>
                        </Stack>
                    </Box>
                    <Collapse in={inventoryForecastOpen} timeout="auto" unmountOnExit>
                        <CardContent sx={{pt: 0, px: {xs: 1.5, sm: 2}, '&:last-child': {pb: 1.5}}}>
                            <Divider sx={{mb: 1}}/>
                            <Stack spacing={0.75}>
                                {forecastShortages.map(item => <Typography key={`${item.type}-${item.itemId}`}>
                                    {itemLabel(item)} — {t('home:missing_quantity', {quantity: formatQuantity(item.shortageQuantity, item.unit)})}
                                </Typography>)}
                            </Stack>
                        </CardContent>
                    </Collapse>
                </Card>}
                <Button fullWidth variant="contained" startIcon={<ShoppingCartOutlinedIcon/>} disabled={!purchasesByDate.length} onClick={() => { setCopyState(null); setPurchaseDialogOpen(true); }}>{t('home:create_purchase_list')}</Button>
            </Stack>}
        </Section>

        <Dialog open={purchaseDialogOpen} onClose={() => setPurchaseDialogOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>{t('home:purchase_list')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    {copyState && <Alert severity={copyState}>{copyState === 'success' ? t('home:copied') : t('home:copy_failed')}</Alert>}
                    <Box sx={{p: 1.5, borderRadius: 2, bgcolor: 'rgba(154, 90, 60, 0.06)', border: 1, borderColor: 'primary.light'}}>
                        <Typography fontWeight={800}>{t('home:purchase_total', {count: period})}</Typography>
                        <PurchaseEstimatedCost value={estimatedPurchaseCost}/>
                        {purchaseTotals.map(item => <Typography key={`${item.type}-${item.itemId}-${item.unit}`} fontWeight={600}>{itemLabel(item)} — {formatQuantity(item.quantity, item.unit)}</Typography>)}
                    </Box>
                    <Divider/>
                    {purchasesByDate.map(([requiredBy, items]) => <Box key={requiredBy}>
                        <Typography fontWeight={700}>{t('home:required_by', {date: formatDate(requiredBy)})}</Typography>
                        {items.map(item => <Typography key={`${item.type}-${item.itemId}-${item.orderId}`}>{itemLabel(item)} — {formatQuantity(item.quantity, item.unit)}</Typography>)}
                    </Box>)}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2, flexWrap: 'wrap'}}>
                <Button onClick={() => setPurchaseDialogOpen(false)}>{t('common:close')}</Button>
                <Button variant="contained" startIcon={<ContentCopyIcon/>} onClick={copyPurchaseList}>{t('home:copy')}</Button>
            </DialogActions>
        </Dialog>
        <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
            <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={serverTraceId}/></Alert>
        </Snackbar>
    </Stack>;
};

export default HomePage;
